import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  SIGN_DOCUMENT_STATUS_LABEL,
  SIGN_MAX_FILES,
  SIGN_MAX_RECIPIENTS,
  SIGN_TITLE_MAX,
  type SignDocumentDetail,
  type SignDocumentSummary,
  type SignDocumentStatus,
  type SignRecipientSummary,
} from '@todo/shared';
import type {
  SignCategory,
  SignInboxEntry,
  UserSummary,
  SignListScope,
} from '@todo/shared';
import { ApiError } from '../api/client';
import { signApi } from '../api/sign.api';
import { userApi } from '../api/user.api';
import { StakeholderSelect } from '../components/StakeholderSelect';
import { Dropdown } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { SidePanel } from '../components/SidePanel';
import { SignFieldEditor } from '../components/SignFieldEditor';
import { SignProgressViewer } from '../components/SignProgressViewer';
import { PdfViewerOverlay } from '../components/PdfViewerOverlay';

/**
 * CG Sign - send documents for signature and follow the envelope through.
 *
 * Since v2: several files per envelope (PDFs and images, merged in order),
 * drag-and-drop upload, recipients ordered BY THEIR ROW (drag to reorder -
 * no order numbers), sequential visibility (a later signer sees nothing
 * until it is their turn), recall-to-draft, and everything opens inside
 * the portal - no more new-tab links.
 */

const RECIPIENT_CHIP: Record<string, string> = {
  PENDING: 'border-slate-300 bg-white text-slate-500',
  NOTIFIED: 'border-slate-300 bg-white text-slate-600',
  SIGNED: 'border-emerald-500 bg-white text-emerald-700',
  DECLINED: 'border-red-400 bg-white text-red-600',
};

/**
 * What the sender's list calls each recipient state.
 *
 * NOTIFIED is shown as MAILED and PENDING as NO_ACTION because those
 * describe what HAPPENED rather than what the row is waiting for -
 * "pending" is true of both, and a list where two different situations
 * read the same is a list that cannot be scanned.
 */
const RECIPIENT_WORD: Record<string, string> = {
  PENDING: 'NO_ACTION',
  NOTIFIED: 'SENT',
  SIGNED: 'SIGNED',
  DECLINED: 'DECLINED',
};

/** Outlined chips for the document column, to sit beside the recipient
 *  ones without two solid blocks of colour fighting in one row. */
const DOC_CHIP: Record<SignDocumentStatus, string> = {
  DRAFT: 'border-slate-300 bg-white text-slate-500',
  SENT: 'border-amber-400 bg-white text-amber-700',
  COMPLETED: 'border-emerald-500 bg-white text-emerald-700',
  DECLINED: 'border-red-400 bg-white text-red-600',
  VOIDED: 'border-slate-300 bg-white text-slate-400',
};

/**
 * The gap between two recorded moments, as "2 days 17 hr 50 min 34 sec".
 *
 * Returns "-" unless BOTH ends were captured: a duration measured from a
 * moment nobody wrote down is a guess, and on a signing trail a guess is
 * worse than a blank. Envelopes sent before the timestamps existed
 * therefore show "-" rather than a fabricated figure.
 */
function gap(from: string | null, to: string | null): string {
  if (!from || !to) return '-';
  let ms = new Date(to).getTime() - new Date(from).getTime();
  // Clocks and backdating can invert a pair; do not print "-3 sec".
  if (!Number.isFinite(ms) || ms < 0) return '-';

  const days = Math.floor(ms / 86_400_000); ms -= days * 86_400_000;
  const hours = Math.floor(ms / 3_600_000); ms -= hours * 3_600_000;
  const mins = Math.floor(ms / 60_000); ms -= mins * 60_000;
  const secs = Math.floor(ms / 1000);

  const parts: string[] = [];
  if (days) parts.push(`${days} day${days === 1 ? '' : 's'}`);
  if (hours) parts.push(`${hours} hr`);
  if (mins) parts.push(`${mins} min`);
  // Seconds always show when nothing bigger did - "0 sec" beats an empty
  // cell for something that genuinely took under a second.
  if (secs || parts.length === 0) parts.push(`${secs} sec`);
  return parts.join(' ');
}

/** One colour per recipient position - the same hues the placement editor
 *  uses for its boxes, so a person keeps their colour everywhere. */
const RECIPIENT_DOT = [
  'bg-amber-400',
  'bg-emerald-400',
  'bg-sky-400',
  'bg-purple-400',
  'bg-rose-400',
  'bg-slate-400',
];
const RECIPIENT_ROW_TINT = [
  'border-amber-200 bg-amber-50/40',
  'border-emerald-200 bg-emerald-50/40',
  'border-sky-200 bg-sky-50/40',
  'border-purple-200 bg-purple-50/40',
  'border-rose-200 bg-rose-50/40',
  'border-slate-200 bg-slate-50/40',
];
const dotOf = (i: number) => RECIPIENT_DOT[i % RECIPIENT_DOT.length];
const tintOf = (i: number) => RECIPIENT_ROW_TINT[i % RECIPIENT_ROW_TINT.length];

interface DraftRecipient {
  displayName: string;
  email: string;
  /**
   * The capacity they sign in - "Manager-IT", "AVP-Finance".
   *
   * Pre-filled from the person's job title when picked from the
   * organisation, and editable: somebody may sign as the authorised
   * approver for one document rather than in their day job. Stored on the
   * recipient as a SNAPSHOT, so a title change later does not rewrite what
   * a past signature claimed.
   */
  designation: string;
}

/** The activity that matters: created / sent / signed / declined /
 *  completed / voided / recalled, plus each person's FIRST view. */
function meaningfulEvents<T extends { action: string; actor: string }>(events: T[]): T[] {
  const seenViewers = new Set<string>();
  return events.filter((e) => {
    if (e.action === 'REQUESTED') return false;
    if (e.action === 'VIEWED') {
      if (seenViewers.has(e.actor)) return false;
      seenViewers.add(e.actor);
    }
    return true;
  });
}

function formatWhen(iso: string): string {
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    .format(new Date(iso));
}

/**
 * The circle-check tracker: one circle per recipient in signing order -
 * filled green with a check once they sign, red on decline, amber pulse on
 * whoever's turn it is, grey while they wait. The joining line fills up to
 * the last completed step.
 */
function SigningProgress({ doc }: { doc: SignDocumentDetail }) {
  if (doc.recipients.length === 0) return null;
  const ordered = [...doc.recipients].sort((a, b) => a.signOrder - b.signOrder);
  const firstOpen = ordered.findIndex((r) => r.status !== 'SIGNED');
  const stateOf = (r: SignRecipientSummary, i: number): 'signed' | 'declined' | 'current' | 'waiting' => {
    if (r.status === 'SIGNED') return 'signed';
    if (r.status === 'DECLINED') return 'declined';
    if (doc.status === 'SENT' && i === firstOpen) return 'current';
    return 'waiting';
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-4">
      <div className="flex items-start">
        {ordered.map((r, i) => {
          const state = stateOf(r, i);
          return (
            <div key={r.id} className="flex min-w-0 flex-1 flex-col items-center">
              <div className="flex w-full items-center">
                <div className={`h-0.5 flex-1 ${i === 0 ? 'invisible' : ''} ${
                  stateOf(ordered[i - 1] ?? r, i - 1) === 'signed' ? 'bg-emerald-400' : 'bg-slate-200'
                }`} />
                {state === 'signed' && (
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-emerald-500 text-white shadow-sm">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4" aria-hidden>
                      <path fillRule="evenodd" d="M16.7 5.3a1 1 0 0 1 0 1.4l-7.5 7.5a1 1 0 0 1-1.4 0L3.3 9.7a1 1 0 1 1 1.4-1.4l3.8 3.8 6.8-6.8a1 1 0 0 1 1.4 0Z" clipRule="evenodd" />
                    </svg>
                  </span>
                )}
                {state === 'declined' && (
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-red-500 text-sm font-bold text-white shadow-sm">
                    &times;
                  </span>
                )}
                {state === 'current' && (
                  <span className="grid h-8 w-8 shrink-0 animate-pulse place-items-center rounded-full border-2 border-amber-500 bg-amber-100 text-xs font-bold text-amber-700">
                    {i + 1}
                  </span>
                )}
                {state === 'waiting' && (
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full border-2 border-slate-300 bg-white text-xs font-semibold text-slate-400">
                    {i + 1}
                  </span>
                )}
                <div className={`h-0.5 flex-1 ${i === ordered.length - 1 ? 'invisible' : ''} ${
                  state === 'signed' ? 'bg-emerald-400' : 'bg-slate-200'
                }`} />
              </div>
              <span className="mt-1.5 w-full truncate px-1 text-center text-[11px] font-medium text-slate-600" title={r.displayName}>
                {r.displayName}
              </span>
              <span className="text-[10px] text-slate-400">
                {state === 'signed' && r.signedAt ? formatWhen(r.signedAt)
                  : state === 'declined' ? 'Declined'
                  : state === 'current' ? 'Their turn'
                  : 'Waiting'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * One header filter: a button that opens a checkbox list.
 *
 * Multi-select rather than a single `<select>` because the questions a
 * sender actually asks are plural - "show me Sent AND Completed", "HR and
 * Finance" - and a one-at-a-time control turns each of those into three
 * separate passes over the list.
 */
function MultiFilter({
  label,
  options,
  selected,
  onChange,
}: {
  label: string;
  options: Array<{ value: string; label: string }>;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);

  // Click-away: a filter popover that survives the next click makes the
  // table underneath unusable.
  useEffect(() => {
    if (!open) return;
    function away(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]);

  return (
    <div ref={box} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 py-1.5 text-xs font-medium transition ${
          selected.length > 0
            ? 'border-brand-300 bg-brand-50 text-brand-700'
            : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400'
        }`}
      >
        {label}
        {selected.length > 0 && (
          <span className="rounded-full bg-brand-600 px-1.5 text-[10px] font-semibold text-white">
            {selected.length}
          </span>
        )}
        <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 opacity-60" aria-hidden>
          <path fillRule="evenodd" d="M5.2 7.2a1 1 0 0 1 1.4 0L10 10.6l3.4-3.4a1 1 0 1 1 1.4 1.4l-4.1 4.1a1 1 0 0 1-1.4 0L5.2 8.6a1 1 0 0 1 0-1.4Z" clipRule="evenodd" />
        </svg>
      </button>

      {/* Left-anchored, and the filters lead the bar: a panel opened from
          the far right of a wide table grows off the side of the window,
          which is what clipped "Information Technology" before. */}
      {open && (
        <div className="absolute left-0 z-20 mt-1.5 max-h-64 w-64 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {options.length === 0 && (
            <p className="px-3 py-2 text-xs text-slate-400">Nothing to filter by yet.</p>
          )}
          {options.map((o) => (
            <label
              key={o.value}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
            >
              <input
                type="checkbox"
                checked={selected.includes(o.value)}
                onChange={() => toggle(o.value)}
                className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="truncate">{o.label}</span>
            </label>
          ))}
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="mt-1 w-full border-t border-slate-100 px-3 py-1.5 text-left text-xs text-slate-500 hover:text-slate-700"
            >
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function SignPage() {
  const { user } = useAuth();
  /** The Stakeholders picker is Admin-only, because the module is. */
  const isAdmin =
    user?.role.systemKey === 'ADMIN' || user?.role.systemKey === 'SUPER_ADMIN';
  const navigate = useNavigate();
  const [documents, setDocuments] = useState<SignDocumentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  // ---- new-document panel ----
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  /** Several files, merged in THIS order (drag rows to rearrange). */
  const [files, setFiles] = useState<File[]>([]);
  const [dragOver, setDragOver] = useState(false);
  const [recipients, setRecipients] = useState<DraftRecipient[]>([
    { displayName: '', email: '', designation: '' },
  ]);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  /** Org members with an email - one click fills a recipient row. */
  const [people, setPeople] = useState<UserSummary[]>([]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  /** A picked stakeholder with no email cannot sign - explain, don't ignore. */
  const [stakeholderNote, setStakeholderNote] = useState<string | null>(null);
  /** What KIND of document this is - reimbursement, food receipt, and so
   *  on. The vocabulary is the organisation's own, so the picker can also
   *  mint a new one inline rather than sending the sender to a settings
   *  page in the middle of a send. */
  const [categoryId, setCategoryId] = useState<string>('');
  const [newCategory, setNewCategory] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);

  /** Index being dragged - recipients and files share the mechanism. */
  const dragRecipient = useRef<number | null>(null);
  const dragFile = useRef<number | null>(null);

  useEffect(() => {
    void userApi
      .list({ pageSize: 100 })
      .then((r) => setPeople(r.data.filter((p) => p.email)))
      .catch(() => undefined);
  }, []);

  // ---- detail panel ----
  const [selected, setSelected] = useState<SignDocumentDetail | null>(null);
  const [busy, setBusy] = useState(false);
  /** Editing a DRAFT from the drawer - after a recall, typically: another
   *  signer and another page before it goes back out. */
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const addFilesRef = useRef<HTMLInputElement | null>(null);
  /** Full-screen placement editor for this draft. */
  const [placing, setPlacing] = useState<SignDocumentDetail | null>(null);
  /** Full-screen live progress view (who signed where, so far). */
  const [viewing, setViewing] = useState<SignDocumentDetail | null>(null);
  /** In-app PDF viewer: the original or the sealed file. */
  const [pdfView, setPdfView] = useState<{
    doc: SignDocumentDetail; kind: 'original' | 'signed';
  } | null>(null);

  /** Envelopes waiting on MY signature - signed right here in the portal.
   *  The API only lists envelopes where it is ALREADY my turn. */
  const [inbox, setInbox] = useState<SignInboxEntry[]>([]);

  /**
   * The header filters. SERVER-side, because the list is paginated:
   * filtering in the browser would only ever filter the page you happen
   * to be looking at.
   */
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<SignDocumentStatus[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [deptFilter, setDeptFilter] = useState<string[]>([]);
  /**
   * "Sign sent" / "Need to sign" - the two questions this page is opened
   * to ask. Mutually exclusive by design: a document cannot be both one
   * you sent and one waiting on your signature, so clicking the second
   * clears the first rather than returning nothing.
   *
   * Server-side like the rest, because the list is paginated.
   */
  const [scope, setScope] = useState<SignListScope | null>(null);
  const [options, setOptions] = useState<{
    categories: SignCategory[]; departments: string[];
  }>({ categories: [], departments: [] });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await signApi.list({
        search: search.trim() || undefined,
        status: statusFilter.length ? statusFilter : undefined,
        categoryIds: categoryFilter.length ? categoryFilter : undefined,
        departments: deptFilter.length ? deptFilter : undefined,
        scope: scope ?? undefined,
      });
      setDocuments(res.data);
      setInbox(await signApi.inbox().catch(() => []));
    } catch {
      setError('Could not load documents.');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, categoryFilter, deptFilter, scope]);

  // Debounced: typing a serial should not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => void load(), 300);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    signApi.filters().then(setOptions).catch(() => undefined);
  }, []);

  function resetForm() {
    setTitle(''); setMessage(''); setFiles([]);
    setRecipients([{ displayName: '', email: '', designation: '' }]);
    setFormError(null);
    setDragOver(false);
    setCategoryId(''); setNewCategory(''); setAddingCategory(false);
  }

  /** Mint a category from the send form. The endpoint is idempotent by
   *  name, so a second sender typing the same word lands on the same row
   *  instead of creating a duplicate. */
  async function createCategory() {
    const name = newCategory.trim();
    if (!name) return;
    setAddingCategory(true);
    try {
      const made = await signApi.createCategory({ name });
      setOptions((o) => ({
        ...o,
        categories: o.categories.some((c) => c.id === made.id)
          ? o.categories
          : [...o.categories, made].sort((a, b) => a.name.localeCompare(b.name)),
      }));
      setCategoryId(made.id);
      setNewCategory('');
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not add that category.');
    } finally {
      setAddingCategory(false);
    }
  }

  const ACCEPTED = /\.(pdf|png|jpe?g)$/i;

  function addFiles(incoming: FileList | File[]) {
    const picked = Array.from(incoming).filter((f) => ACCEPTED.test(f.name));
    if (picked.length === 0) {
      setFormError('Only PDF, PNG or JPEG files can be added.');
      return;
    }
    setFormError(null);
    setFiles((fs) => {
      const merged = [...fs];
      for (const f of picked) {
        if (merged.some((x) => x.name === f.name && x.size === f.size)) continue;
        if (merged.length >= SIGN_MAX_FILES) break;
        merged.push(f);
      }
      return merged;
    });
  }

  function reorder<T>(list: T[], from: number, to: number): T[] {
    const next = [...list];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved as T);
    return next;
  }

  /**
   * Create the draft (+ recipients), then either stop as a draft or open
   * the PLACEMENT editor, where "Save & send" finishes the job. The row
   * order IS the signing order - first row signs first.
   */
  async function createDocument(thenPlace: boolean) {
    setFormError(null);
    if (!title.trim()) { setFormError('Give the document a title.'); return; }
    if (files.length === 0) { setFormError('Add at least one file (PDF or image).'); return; }
    const filled = recipients.filter((r) => r.email.trim() && r.displayName.trim());
    if (thenPlace && filled.length === 0) { setFormError('Add at least one recipient.'); return; }

    setSaving(true);
    try {
      const doc = await signApi.create(title.trim(), message.trim(), files, categoryId || null);
      if (filled.length > 0) {
        await signApi.setRecipients(doc.id, {
          recipients: filled.map((r) => ({
            email: r.email.trim(),
            displayName: r.displayName.trim(),
            // Blank means "not stated" rather than an empty string, so the
            // column reads NULL and nothing renders a stray separator.
            designation: r.designation.trim() || null,
          })),
        });
      }
      setCreating(false);
      resetForm();
      void load();
      if (thenPlace) {
        setPlacing(await signApi.detail(doc.id));
      } else {
        setFlash(`"${doc.title}" saved as a draft.`);
      }
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not create the document.');
    } finally {
      setSaving(false);
    }
  }

  /** True while the certificate PDF is being built server-side. */
  const [downloadingCert, setDownloadingCert] = useState(false);

  /**
   * Fetch the certificate and hand it to the browser as a download.
   *
   * It travels as bytes rather than a SAS URL because it is generated on
   * request - there is no blob to link to, and building one would be a
   * second copy of something the columns already say.
   */
  async function downloadCertificate(id: string) {
    setDownloadingCert(true);
    try {
      const { bytes, fileName } = await signApi.certificate(id);
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      // Revoke on the next tick - revoking synchronously can beat the
      // download in some browsers.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'Could not build the certificate.');
    } finally {
      setDownloadingCert(false);
    }
  }

  async function openDetail(id: string) {
    try {
      setSelected(await signApi.detail(id));
    } catch {
      setFlash('Could not open that document.');
    }
  }

  async function act(action: () => Promise<unknown>, done: string) {
    if (!selected) return;
    setBusy(true);
    try {
      await action();
      setFlash(done);
      setSelected(await signApi.detail(selected.id).catch(() => null as never));
      void load();
    } catch (err) {
      setFlash(err instanceof ApiError ? err.message : 'That did not work - try again.');
    } finally {
      setBusy(false);
    }
  }

  const nobodySigned = selected
    ? selected.recipients.every((r) => r.status !== 'SIGNED')
    : false;

  /**
   * Recipients on the open draft with no SIGNATURE box placed for them.
   *
   * "Send now" in this drawer bypasses the placement editor, so the same
   * rule the editor enforces has to hold here too: every person being
   * asked to sign needs somewhere to sign. (The API refuses either way -
   * this only keeps the refusal from being a surprise.)
   */
  const unplacedSigners = selected
    ? selected.recipients
        .filter((r) => !selected.fields.some((f) => f.recipientId === r.id && f.kind === 'SIGNATURE'))
        .sort((a, b) => a.signOrder - b.signOrder)
    : [];
  const readyToSend =
    selected !== null && selected.recipients.length > 0 && unplacedSigners.length === 0;

  return (
    <div className="mx-auto px-8 py-8">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">CG Sign</h1>
          <p className="mt-1 text-sm text-slate-500">
            Send documents for signature. Recipients sign through a personal link - no account needed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { resetForm(); setCreating(true); }}
          className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-brand-700"
        >
          New document
        </button>
      </header>

      {flash && (
        <div role="status" className="mt-5 flex items-start justify-between gap-3 rounded-lg bg-emerald-50 px-3.5 py-2.5 text-sm text-emerald-800 ring-1 ring-emerald-200">
          <span>{flash}</span>
          <button type="button" onClick={() => setFlash(null)} aria-label="Dismiss"
                  className="text-emerald-600 hover:text-emerald-800">&times;</button>
        </div>
      )}
      {error && (
        <p role="alert" className="mt-5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">{error}</p>
      )}

      {/* ---- awaiting MY signature: sign right here, no email needed ---- */}
      {inbox.length > 0 && (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">
            Awaiting your signature
          </p>
          <ul className="mt-2 space-y-2">
            {inbox.map((entry) => (
              <li
                key={entry.token}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-white px-3.5 py-2.5"
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-slate-900">{entry.title}</span>
                  <span className="block text-xs text-slate-500">
                    from {entry.ownerName}
                    {entry.sentAt ? ` · sent ${formatWhen(entry.sentAt)}` : ''}
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => navigate(`/sign/${entry.token}`)}
                  className="rounded-lg bg-brand-600 px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700"
                >
                  Sign now
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/*
        One row per RECIPIENT, with the document name and status spanning
        its own recipients. A row per document with "2/5 signed" answers
        the wrong question: what a sender opens this page to find out is
        WHO is holding it up, and that is a per-person fact.

        Both durations are computed here from timestamps the API already
        returns - no extra call, and no server-side arithmetic to keep in
        step with what the screen shows.
      */}
      <div className="mt-6 rounded-xl border border-slate-200 bg-white">
        {/* Header filters. Every one of them is applied SERVER-side and
            they combine with AND, so narrowing by two headers narrows the
            whole list rather than the page currently on screen. */}
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-200 px-4 py-3">
          {/*
            The two scope buttons lead the bar, before the column filters.
            They answer the questions people actually arrive with - what
            did I send, and what is waiting on me - where the filters
            behind them answer "show me a slice of everything".

            TOGGLES, not a three-way control: clicking the active one
            clears it and returns to everything. And mutually exclusive,
            because nothing is both sent by you and awaiting your
            signature, so allowing both would only ever show nothing.
          */}
          {([
            ['SENT_BY_ME', 'Sign sent'],
            ['NEEDS_MY_SIGNATURE', 'Need to sign'],
          ] as const).map(([value, label]) => {
            const active = scope === value;
            return (
              <button
                key={value}
                type="button"
                aria-pressed={active}
                onClick={() => setScope(active ? null : value)}
                className={[
                  'rounded-full border px-3 py-1.5 text-xs font-semibold transition',
                  active
                    ? 'border-brand-500 bg-brand-50 text-brand-700'
                    : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 hover:bg-slate-50',
                ].join(' ')}
              >
                {label}
              </button>
            );
          })}

          <span aria-hidden className="mx-1 h-5 w-px bg-slate-200" />

          <MultiFilter
            label="Status"
            selected={statusFilter}
            onChange={(next) => setStatusFilter(next as SignDocumentStatus[])}
            options={(Object.keys(SIGN_DOCUMENT_STATUS_LABEL) as SignDocumentStatus[]).map((k) => ({
              value: k,
              label: SIGN_DOCUMENT_STATUS_LABEL[k],
            }))}
          />
          <MultiFilter
            label="Category"
            selected={categoryFilter}
            onChange={setCategoryFilter}
            options={[
              { value: 'none', label: 'Uncategorised' },
              ...options.categories.map((c) => ({ value: c.id, label: c.name })),
            ]}
          />
          <MultiFilter
            label="Department"
            selected={deptFilter}
            onChange={setDeptFilter}
            options={options.departments.map((d) => ({ value: d, label: d }))}
          />

          {/* Search last, and it takes the remaining width: the three
              dropdowns are fixed-size and sit at the start, so their
              panels open well inside the card instead of off the edge. */}
          <div className="relative min-w-[220px] flex-1">
            <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden
                 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400">
              <path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 3.4 9.8l3.2 3.2a1 1 0 0 0 1.4-1.4l-3.2-3.2A5.5 5.5 0 0 0 9 3.5Zm-3.5 5.5a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0Z" clipRule="evenodd" />
            </svg>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search title, file name or serial number"
              className="w-full rounded-lg border border-slate-300 py-1.5 pl-9 pr-3 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
            />
          </div>

          {(search || statusFilter.length || categoryFilter.length || deptFilter.length) ? (
            <button
              type="button"
              onClick={() => { setSearch(''); setStatusFilter([]); setCategoryFilter([]); setDeptFilter([]); }}
              className="whitespace-nowrap rounded-lg px-2.5 py-1.5 text-xs font-medium text-slate-500 transition hover:text-slate-800"
            >
              Reset filters
            </button>
          ) : null}
        </div>

        <div className="overflow-x-auto">
        <table className="w-full min-w-[1180px] text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50/70 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3 font-semibold">Document name</th>
              <th className="px-4 py-3 font-semibold">Serial no.</th>
              <th className="px-4 py-3 font-semibold">Category</th>
              <th className="px-4 py-3 font-semibold">Raised by</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Recipients</th>
              <th className="px-4 py-3 font-semibold">Recipient status</th>
              <th className="px-4 py-3 font-semibold">Signing interval</th>
              <th className="px-4 py-3 font-semibold">Time taken to sign</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">Loading…</td></tr>
            )}
            {!loading && documents.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-400">
                {/* The scope buttons get their own words. "No document
                    matches these filters" is true but unhelpful when the
                    answer is the good news that you are up to date. */}
                {scope === 'NEEDS_MY_SIGNATURE'
                  ? 'Nothing is waiting on your signature right now.'
                  : scope === 'SENT_BY_ME'
                    ? 'You have not sent any documents for signature yet.'
                    : search || statusFilter.length || categoryFilter.length || deptFilter.length
                      ? 'No document matches these filters.'
                      : 'Nothing yet - send the first document for signature.'}
              </td></tr>
            )}
            {!loading && documents.map((doc) => {
              // A document with nobody on it still needs a row, or it
              // vanishes from the list entirely.
              const rows = doc.recipients.length > 0
                ? [...doc.recipients].sort((a, b) => a.signOrder - b.signOrder)
                : [null];
              return rows.map((r, i) => (
                <tr
                  key={r ? r.id : doc.id}
                  onClick={() => void openDetail(doc.id)}
                  className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50/70"
                >
                  {/* Spanned cells are rendered ONCE, on the first
                      recipient row - that is what visually groups the
                      block without a nested table. */}
                  {i === 0 && (
                    <>
                      <td rowSpan={rows.length} className="border-r border-slate-100 px-4 py-3 align-middle">
                        <span className="block max-w-[260px] truncate font-medium text-slate-900" title={doc.title}>
                          {doc.title}
                        </span>
                        <span className="block max-w-[260px] truncate text-xs text-slate-400" title={doc.fileName}>
                          {doc.fileName}
                        </span>
                      </td>
                      <td rowSpan={rows.length} className="border-r border-slate-100 px-4 py-3 align-middle">
                        {/* Printed on every page of the envelope, so the
                            paper on someone's desk can be traced back to
                            this row. */}
                        <span className="whitespace-nowrap font-mono text-xs text-slate-600">
                          {doc.serialNo ?? '-'}
                        </span>
                      </td>
                      <td rowSpan={rows.length} className="border-r border-slate-100 px-4 py-3 align-middle">
                        {doc.categoryName ? (
                          <span className="inline-block max-w-[150px] truncate rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                                title={doc.categoryName}>
                            {doc.categoryName}
                          </span>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td rowSpan={rows.length} className="border-r border-slate-100 px-4 py-3 align-middle">
                        <span className="block max-w-[170px] truncate text-slate-700" title={doc.ownerName}>
                          {doc.ownerName}
                        </span>
                        <span className="block max-w-[170px] truncate text-xs text-slate-400"
                              title={doc.ownerDepartment ?? undefined}>
                          {doc.ownerDepartment ?? 'No department'}
                        </span>
                      </td>
                      <td rowSpan={rows.length} className="border-r border-slate-100 px-4 py-3 align-middle">
                        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold ${DOC_CHIP[doc.status]}`}>
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          {SIGN_DOCUMENT_STATUS_LABEL[doc.status].toUpperCase()}
                        </span>
                      </td>
                    </>
                  )}

                  {r === null ? (
                    <td colSpan={4} className="px-4 py-3 text-xs text-slate-400">
                      No recipients yet
                    </td>
                  ) : (
                    <>
                      <td className="px-4 py-3">
                        <span className="block max-w-[260px] truncate text-slate-700" title={r.email}>
                          {r.email}
                        </span>
                        {r.displayName && r.displayName !== r.email && (
                          <span className="block max-w-[260px] truncate text-xs text-slate-400">
                            {r.signOrder}. {r.displayName}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold ${RECIPIENT_CHIP[r.status]}`}>
                          <span className="h-1.5 w-1.5 rounded-full bg-current" />
                          {RECIPIENT_WORD[r.status]}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        {gap(r.notifiedAt, r.signedAt)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-slate-600">
                        {gap(r.viewedAt, r.signedAt)}
                      </td>
                    </>
                  )}
                </tr>
              ));
            })}
          </tbody>
        </table>
        </div>
      </div>

      {/* ---- new document ---- */}
      <SidePanel
        open={creating}
        title="New document"
        description="Add files and choose who signs, in order."
        closeOnBackdropClick={false}
        onClose={() => setCreating(false)}
      >
        <div className="flex h-full flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">
            {formError && (
              <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-100">{formError}</p>
            )}
            <div>
              <label htmlFor="sign-title" className="block text-sm font-medium text-slate-700">
                Title <span className="ml-1 text-red-500">*</span>
              </label>
              <input id="sign-title" value={title} maxLength={SIGN_TITLE_MAX}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. NDA - Acme Pvt Ltd"
                className="mt-1.5 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
            </div>

            {/* ---- what kind of document this is ---- */}
            <div>
              <label htmlFor="sign-category" className="block text-sm font-medium text-slate-700">
                Category
              </label>
              <div className="mt-1.5 flex gap-2">
                {/* The org's filing vocabulary grows with the documents,
                    so this list is not small for long. */}
                <Dropdown
                  className="w-full"
                  value={categoryId || ''}
                  onChange={setCategoryId}
                  placeholder="No category"
                  searchPlaceholder="Search categories…"
                  options={[
                    { value: '', label: 'No category' },
                    ...options.categories.map((c) => ({ value: c.id, label: c.name })),
                  ]}
                />
              </div>
              {/* Inline creation: the vocabulary is the organisation's own
                  and it grows as documents arrive, so a sender who needs a
                  word that does not exist yet should not have to leave. */}
              <div className="mt-2 flex gap-2">
                <input
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); void createCategory(); }
                  }}
                  placeholder="or add one - e.g. Reimbursement"
                  className="w-full rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100"
                />
                <button
                  type="button"
                  onClick={() => void createCategory()}
                  disabled={!newCategory.trim() || addingCategory}
                  className="whitespace-nowrap rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:border-slate-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {addingCategory ? 'Adding…' : 'Add'}
                </button>
              </div>
            </div>

            {/* ---- files: drag-and-drop, several, PDFs and images ---- */}
            <div>
              <p className="block text-sm font-medium text-slate-700">
                Files <span className="ml-1 text-red-500">*</span>
              </p>
              <div
                role="button"
                tabIndex={0}
                onClick={() => fileInputRef.current?.click()}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click(); }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  addFiles(e.dataTransfer.files);
                }}
                className={`mt-1.5 flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-4 py-6 text-center transition ${
                  dragOver
                    ? 'border-brand-500 bg-brand-50'
                    : 'border-slate-300 bg-slate-50 hover:border-brand-400 hover:bg-brand-50/40'
                }`}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"
                     className="h-7 w-7 text-slate-400" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round"
                        d="M12 16V4m0 0-4 4m4-4 4 4M4 16v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                </svg>
                <p className="mt-2 text-sm font-medium text-slate-700">
                  Drop files here, or click to choose
                </p>
                <p className="mt-0.5 text-xs text-slate-500">
                  PDF, PNG or JPEG · up to {SIGN_MAX_FILES} files · merged into one document in this order
                </p>
                <input ref={fileInputRef} type="file" multiple hidden
                  accept="application/pdf,image/png,image/jpeg"
                  onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }} />
              </div>
              {files.length > 0 && (
                <ul className="mt-2 space-y-1.5">
                  {files.map((f, i) => (
                    <li
                      key={`${f.name}-${f.size}`}
                      draggable
                      onDragStart={() => { dragFile.current = i; }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const from = dragFile.current;
                        dragFile.current = null;
                        if (from === null || from === i) return;
                        setFiles((fs) => reorder(fs, from, i));
                      }}
                      className="flex cursor-grab items-center gap-2 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 active:cursor-grabbing"
                    >
                      <span className="text-slate-300" aria-hidden>⠿</span>
                      <span className="grid h-5 w-5 shrink-0 place-items-center rounded bg-slate-100 text-[10px] font-bold text-slate-500">
                        {i + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-sm text-slate-700">{f.name}</span>
                      <span className="text-[11px] text-slate-400">{(f.size / 1024 / 1024).toFixed(1)} MB</span>
                      <button type="button" aria-label={`Remove ${f.name}`}
                        onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}
                        className="rounded px-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
                        &times;
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <p className="text-sm font-medium text-slate-700">Recipients</p>
              <p className="mt-0.5 text-xs text-slate-500">
                The list order IS the signing order - the first row signs
                first, and each person only sees the document once everyone
                above them has signed. Drag rows to reorder.
              </p>

              {people.length > 0 && (
                <Dropdown
                  value={undefined}
                  onChange={(pickedId) => {
                    const person = people.find((p) => p.id === pickedId);
                    if (!person || !person.email) return;
                    setRecipients((rs) => {
                      // Already listed? Do nothing rather than duplicate.
                      if (rs.some((r) => r.email.toLowerCase() === person.email!.toLowerCase())) return rs;
                      // Fill the first EMPTY row before adding a new one.
                      const emptyAt = rs.findIndex((r) => !r.displayName.trim() && !r.email.trim());
                      // Their job title is the usual answer for "signing as
                      // what?", so it is offered rather than demanded - the
                      // sender can overwrite it per document.
                      const filled = {
                        displayName: person.displayName,
                        email: person.email!,
                        designation: person.jobTitle ?? '',
                      };
                      return emptyAt >= 0
                        ? rs.map((r, i) => (i === emptyAt ? filled : r))
                        : [...rs, filled];
                    });
                  }}
                  options={people.map((p) => ({
                    value: p.id,
                    label: p.displayName,
                    /* The email is the second line, not glued onto the
                       name - and it is searchable, which is how you find
                       the right Kumar. */
                    description: p.email ?? undefined,
                  }))}
                  className="mt-2"
                  placeholder="Add from organisation"
                  searchPlaceholder="Search people…"
                />
              )}

              {/* External signers from Stakeholders - investors signing an
                  agreement, media signing a release. Admin-only, like the
                  module itself. */}
              <StakeholderSelect
                enabled={isAdmin}
                exclude={[]}
                onPick={(o) => {
                  if (!o.email) {
                    setStakeholderNote(
                      `${o.displayName} has no email on file - add one in Stakeholders first.`,
                    );
                    return;
                  }
                  setStakeholderNote(null);
                  setRecipients((rs) => {
                    if (rs.some((r) => r.email.toLowerCase() === o.email!.toLowerCase())) return rs;
                    const emptyAt = rs.findIndex((r) => !r.displayName.trim() && !r.email.trim());
                    // A stakeholder carries no job title - left blank for
                    // the sender to fill, rather than guessed.
                    const filled = { displayName: o.displayName, email: o.email!, designation: '' };
                    return emptyAt >= 0
                      ? rs.map((r, i) => (i === emptyAt ? filled : r))
                      : [...rs, filled];
                  });
                }}
              />
              {stakeholderNote && (
                <p className="mt-1.5 text-xs text-amber-700">{stakeholderNote}</p>
              )}
              <div className="mt-2 space-y-2">
                {recipients.map((r, i) => (
                  <div
                    key={i}
                    draggable
                    onDragStart={() => { dragRecipient.current = i; }}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = dragRecipient.current;
                      dragRecipient.current = null;
                      if (from === null || from === i) return;
                      setRecipients((rs) => reorder(rs, from, i));
                    }}
                    className={`flex cursor-grab items-start gap-2 rounded-lg border px-2 py-1.5 active:cursor-grabbing ${tintOf(i)}`}
                  >
                    <span className="mt-2 text-slate-300" aria-hidden>⠿</span>
                    <span className={`mt-1.5 grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white ${dotOf(i)}`}>
                      {i + 1}
                    </span>
                    {/*
                      TWO LINES, not three fields across. This panel is a
                      sidebar; three inputs on one row leaves each of them
                      too narrow to read an email address in. Name and
                      designation pair naturally (they are what gets printed
                      side by side), with the email - the longest value - on
                      its own full-width line.
                    */}
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex gap-1.5">
                        <input value={r.displayName} placeholder="Name"
                          onChange={(e) => setRecipients((rs) => rs.map((x, j) => j === i ? { ...x, displayName: e.target.value } : x))}
                          className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm outline-none transition focus:border-brand-500" />
                        <input value={r.designation} placeholder="Designation"
                          title="The capacity they sign in - e.g. Manager-IT. Optional."
                          onChange={(e) => setRecipients((rs) => rs.map((x, j) => j === i ? { ...x, designation: e.target.value } : x))}
                          className="min-w-0 flex-1 rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm outline-none transition focus:border-brand-500" />
                      </div>
                      <input value={r.email} placeholder="Email" type="email"
                        onChange={(e) => setRecipients((rs) => rs.map((x, j) => j === i ? { ...x, email: e.target.value } : x))}
                        className="w-full rounded-lg border border-slate-300 bg-white px-2.5 py-2 text-sm outline-none transition focus:border-brand-500" />
                    </div>
                    <button type="button" aria-label="Remove recipient"
                      onClick={() => setRecipients((rs) => rs.filter((_, j) => j !== i))}
                      className="mt-1 rounded-lg px-2 py-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
                      &times;
                    </button>
                  </div>
                ))}
              </div>
              {recipients.length < SIGN_MAX_RECIPIENTS && (
                <button type="button"
                  onClick={() => setRecipients((rs) => [...rs, { displayName: '', email: '', designation: '' }])}
                  className="mt-2 text-sm font-medium text-brand-600 hover:text-brand-700">
                  + Add recipient
                </button>
              )}
            </div>

            <div>
              <label htmlFor="sign-message" className="block text-sm font-medium text-slate-700">
                Message to signers <span className="ml-1 text-slate-400">(optional)</span>
              </label>
              <textarea id="sign-message" value={message} rows={3} maxLength={1000}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Anything they should know before signing"
                className="mt-1.5 w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100" />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
            <button type="button" onClick={() => setCreating(false)}
              className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50">
              Cancel
            </button>
            <button type="button" disabled={saving} onClick={() => void createDocument(false)}
              className="rounded-lg border border-slate-300 px-3.5 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 disabled:opacity-50">
              Save draft
            </button>
            <button type="button" disabled={saving} onClick={() => void createDocument(true)}
              className="rounded-lg bg-brand-600 px-3.5 py-2 text-sm font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50">
              {saving ? 'Preparing…' : 'Place signatures →'}
            </button>
          </div>
        </div>
      </SidePanel>

      {/* ---- detail ---- */}
      <SidePanel
        open={selected !== null}
        title={selected?.title ?? ''}
        description={selected ? `${SIGN_DOCUMENT_STATUS_LABEL[selected.status]} · sent by ${selected.ownerName}` : undefined}
        onClose={() => setSelected(null)}
      >
        {selected && (
          <div className="space-y-6 px-6 py-5">
            {/* Why "Send now" is greyed out, in words. A disabled button
                with no explanation reads as a bug. */}
            {selected.status === 'DRAFT' && selected.ownerId === user?.id && !readyToSend && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 ring-1 ring-amber-200">
                {selected.recipients.length === 0
                  ? 'Add a recipient, then place a signature box, before this can be sent.'
                  : `Not ready to send - no signature box has been placed for ${
                      unplacedSigners.map((r) => `${r.signOrder}. ${r.displayName}`).join(', ')
                    }. Use "Place signatures".`}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <button type="button"
                onClick={() => setPdfView({ doc: selected, kind: 'original' })}
                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50">
                View original
              </button>
              {selected.overlays.length > 0 && selected.status !== 'DRAFT' && (
                <button type="button"
                  onClick={() => { setViewing(selected); setSelected(null); }}
                  className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100">
                  View progress
                </button>
              )}
              {selected.signedFileUrl && (
                <button type="button"
                  onClick={() => setPdfView({ doc: selected, kind: 'signed' })}
                  className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-emerald-700">
                  View signed PDF
                </button>
              )}
              {/* A file of its own, not a page of the contract - the
                  signed agreement gets forwarded and printed, and the
                  record ABOUT the signing does not belong stapled to it. */}
              {selected.status === 'COMPLETED' && (
                <button type="button" disabled={downloadingCert}
                  onClick={() => void downloadCertificate(selected.id)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:opacity-50">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
                       className="h-3.5 w-3.5" aria-hidden>
                    <path strokeLinecap="round" strokeLinejoin="round"
                          d="M12 15V3m0 12-3.5-3.5M12 15l3.5-3.5M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
                  </svg>
                  {downloadingCert ? 'Preparing…' : 'Certificate of Completion'}
                </button>
              )}
              {/* Envelopes sealed before the certificate was split out
                  still carry it as a trailing page. Rebuilding drops it;
                  the fingerprint changes, so it is Super Admin only and
                  the old and new hashes go into the trail. */}
              {selected.status === 'COMPLETED' && user?.role.systemKey === 'SUPER_ADMIN' && (
                <button type="button" disabled={busy}
                  onClick={() => void act(
                    () => signApi.reseal(selected.id),
                    'Sealed PDF rebuilt without the certificate page.',
                  )}
                  className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50">
                  Rebuild sealed PDF
                </button>
              )}
              {selected.ownerId === user?.id && selected.status === 'DRAFT' && (
                <>
                  <button type="button" disabled={busy}
                    onClick={() => { setPlacing(selected); setSelected(null); }}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50">
                    Place signatures
                  </button>
                  <input ref={addFilesRef} type="file" multiple hidden
                    accept="application/pdf,image/png,image/jpeg"
                    onChange={(e) => {
                      const picked = Array.from(e.target.files ?? []);
                      e.target.value = '';
                      if (!picked.length) return;
                      void act(
                        () => signApi.addFiles(selected.id, picked),
                        `${picked.length} file(s) added - they are the last pages now.`,
                      );
                    }} />
                  <button type="button" disabled={busy}
                    onClick={() => addFilesRef.current?.click()}
                    className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50">
                    Add files
                  </button>
                  <button
                    type="button"
                    disabled={busy || !readyToSend}
                    onClick={() => void act(() => signApi.send(selected.id), 'Sent for signature.')}
                    title={
                      readyToSend
                        ? undefined
                        : selected.recipients.length === 0
                          ? 'Add at least one recipient first'
                          : `No signature box yet for ${unplacedSigners.map((r) => r.displayName).join(', ')}`
                    }
                    className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50">
                    Send now
                  </button>
                  <button type="button" disabled={busy}
                    onClick={() => void act(async () => { await signApi.removeDraft(selected.id); setSelected(null); }, 'Draft deleted.')}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50">
                    Delete draft
                  </button>
                </>
              )}
              {selected.ownerId === user?.id && selected.status === 'SENT' && (
                <>
                  {nobodySigned && (
                    <button type="button" disabled={busy}
                      onClick={() => void act(() => signApi.recall(selected.id), 'Recalled - the document is a draft again.')}
                      className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-700 transition hover:bg-amber-100 disabled:opacity-50">
                      Recall to draft
                    </button>
                  )}
                  <button type="button" disabled={busy}
                    onClick={() => void act(() => signApi.void(selected.id), 'Document voided.')}
                    className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 transition hover:bg-red-50 disabled:opacity-50">
                    Void
                  </button>
                </>
              )}
            </div>

            {/* ---- circle-check progress: one step per signer, in order ---- */}
            <SigningProgress doc={selected} />

            {selected.finalSha256 && (
              <p className="break-all rounded-lg bg-slate-50 px-3 py-2 text-[11px] text-slate-500 ring-1 ring-slate-200">
                SHA-256 fingerprint: {selected.finalSha256}
              </p>
            )}

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Recipients</p>
              <ul className="mt-2 space-y-2">
                {selected.recipients.map((r, i) => (
                  <li key={r.id} className={`flex items-center justify-between gap-2 rounded-lg border px-3 py-2 ${tintOf(i)}`}>
                    <span className="flex min-w-0 items-center gap-2.5">
                      <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold text-white ${dotOf(i)}`}>
                        {r.signOrder}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium text-slate-900">{r.displayName}</span>
                        <span className="block truncate text-xs text-slate-400">{r.email}</span>
                        {r.status === 'SIGNED' && (r.ip || r.device) && (
                          <span className="block text-[11px] text-slate-500">
                            {[r.device, r.ip].filter(Boolean).join(' · ')}
                          </span>
                        )}
                        {r.declineReason && (
                          <span className="block text-xs text-red-600">Declined: {r.declineReason}</span>
                        )}
                      </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-1.5">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${RECIPIENT_CHIP[r.status] ?? ''}`}>
                        {r.status === 'SIGNED' && r.signedAt ? `Signed ${formatWhen(r.signedAt)}` : r.status.toLowerCase()}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>

              {selected.ownerId === user?.id
                && selected.status === 'DRAFT'
                && selected.recipients.length < SIGN_MAX_RECIPIENTS && (
                <div className="mt-2 space-y-2">
                  {people.length > 0 && (
                    <Dropdown
                      value={undefined}
                      onChange={(pickedId) => {
                        const person = people.find((x) => x.id === pickedId);
                        if (!person?.email) return;
                        // Fills the boxes rather than submitting - one look
                        // before a signing link goes out.
                        setAddName(person.displayName);
                        setAddEmail(person.email);
                      }}
                      placeholder="Add from organisation"
                      searchPlaceholder="Search people…"
                      options={people
                        .filter((x) => !selected.recipients.some(
                          (r) => r.email.toLowerCase() === x.email!.toLowerCase(),
                        ))
                        .map((x) => ({
                          value: x.id,
                          label: x.displayName,
                          description: x.email ?? undefined,
                        }))}
                    />
                  )}
                  <div className="flex items-center gap-2">
                  <input value={addName}
                    onChange={(e) => setAddName(e.target.value)}
                    placeholder="Name"
                    className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none" />
                  <input value={addEmail} type="email"
                    onChange={(e) => setAddEmail(e.target.value)}
                    placeholder="Email"
                    className="min-w-0 flex-1 rounded-lg border border-slate-300 px-2 py-1.5 text-xs focus:border-brand-500 focus:outline-none" />
                  <button type="button"
                    disabled={busy || !addName.trim() || !addEmail.trim()}
                    onClick={() => void act(
                      async () => {
                        await signApi.addRecipient(selected.id, {
                          displayName: addName.trim(),
                          email: addEmail.trim(),
                        });
                        setAddName('');
                        setAddEmail('');
                      },
                      'Recipient added - they sign last.',
                    )}
                    className="shrink-0 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-brand-700 disabled:opacity-50">
                    Add
                  </button>
                  </div>
                </div>
              )}
            </div>

            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Activity</p>
              <ul className="mt-2 space-y-1.5">
                {meaningfulEvents(selected.events).map((e, i) => (
                  <li key={i} className="text-xs text-slate-600">
                    <span className="text-slate-400">{formatWhen(e.createdAt)}</span>{' '}
                    <span className="font-medium">{e.action.toLowerCase()}</span>{' '}
                    {e.actor}{e.detail ? ` — ${e.detail}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </SidePanel>

      {/* ---- full-screen live progress view ---- */}
      {viewing && <SignProgressViewer document={viewing} onClose={() => setViewing(null)} />}

      {/* ---- in-app PDF viewer (original / sealed) ---- */}
      {pdfView && (
        <PdfViewerOverlay
          title={pdfView.doc.title}
          subtitle={pdfView.kind === 'signed' ? 'Signed & sealed PDF' : `Original · ${pdfView.doc.fileName}`}
          loadBytes={() =>
            pdfView.kind === 'signed'
              ? signApi.signedFileBytes(pdfView.doc.id)
              : signApi.fileBytes(pdfView.doc.id)
          }
          downloadName={
            pdfView.kind === 'signed'
              ? `signed-${pdfView.doc.fileName.replace(/\.pdf$/i, '')}.pdf`
              : pdfView.doc.fileName
          }
          onClose={() => setPdfView(null)}
        />
      )}

      {/* ---- full-screen placement editor ---- */}
      {placing && (
        <SignFieldEditor
          document={placing}
          onClose={() => setPlacing(null)}
          onSent={() => {
            setPlacing(null);
            setFlash('Sent for signature.');
            void load();
          }}
        />
      )}
    </div>
  );
}
